/* ==========================================================================
   Casemira C3 - vague 5 : la transition collection → fiche
   (point 13 de la SPEC, règle 12 du benchmark)
   --------------------------------------------------------------------------
   AMÉLIORATION PROGRESSIVE, au sens strict. Sans cette API, le clic sur une
   carte reste un clic sur un lien : la fiche s'ouvre exactement comme avant,
   au même moment. Rien ici ne retarde son affichage, rien n'est requis pour
   qu'elle fonctionne. Firefox ne connaît pas `@view-transition` : il ne verra
   jamais rien de ce fichier, et c'est très bien.
   Support mesuré (caniuse, 25/08/2026) : Chrome et Edge 126+, Safari 18.2+,
   Opera 112+, Samsung 28+, soit ~85 % du trafic mondial - et la quasi-totalité
   du trafic de Casemira, qui est Safari iOS et Chrome Android.

   PORTÉE : deux gabarits, pas le site.
   L'opt-in `@view-transition { navigation: auto }` n'est déclaré que dans
   `casemira-collection.css` et `casemira-pdp-layout.css`, chargés par la seule
   section de collection et la seule section de fiche. Ce fichier est chargé
   par les deux mêmes sections. Une navigation entre deux pages quelconques du
   site n'a donc aucun opt-in à lire.

   Et à l'intérieur de ces deux gabarits, on referme encore : une collection
   mène aussi à une autre collection (pagination, sous-collection), une fiche
   mène au panier, à une autre fiche, au compte. Ces navigations-là sont
   annulées à la volée dans `pageswap` (`skipTransition()`). Il ne reste que
   collection → fiche et fiche → collection.

   LE NOM, ET POURQUOI IL EST UNIQUE
   `view-transition-name` doit être unique dans un document, sinon le navigateur
   abandonne la paire - silencieusement. On ne nomme donc jamais qu'UN cadre par
   page : celui de la carte cliquée d'un côté, celui de la photo affichée de
   l'autre, tous deux `product-<handle>`. Le carrousel de la fiche peut dupliquer
   ses diapositives (`.swiper-slide-duplicate`) : elles sont écartées.

   MOUVEMENT RÉDUIT
   `prefers-reduced-motion` : aucun nom n'est posé, il ne reste que le fondu de
   la racine. Le déplacement d'une grande image d'un bout à l'autre de l'écran
   est précisément ce que ce réglage demande d'éviter.
   ========================================================================== */

(function () {
  'use strict';

  /* Chargé par DEUX sections (collection et fiche) : jamais par la même page,
     mais le garde-fou coûte deux lignes. */
  if (window.__cmVtInit) return;
  window.__cmVtInit = true;

  /* Le test qui décide de tout : sans l'API, ce fichier ne fait rien du tout. */
  if (typeof document.startViewTransition !== 'function') return;
  if (!('onpageswap' in window) && !('onpagereveal' in window)) return;

  var STORE = 'casemira:c3-vt';
  var ATTR = 'data-cm-vt-named';

  function reduced() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function isProductUrl(href) {
    try {
      return /\/products\//.test(new URL(href, window.location.origin).pathname);
    } catch (e) {
      return false;
    }
  }

  function isCollectionUrl(href) {
    try {
      var path = new URL(href, window.location.origin).pathname;
      return /\/collections\//.test(path) && !/\/products\//.test(path);
    } catch (e) {
      return false;
    }
  }

  function nameFor(handle) {
    /* Les handles Shopify sont déjà en minuscules ASCII avec des traits ; le
       filtre ne fait que garantir un `<custom-ident>` valide. Le préfixe
       garantit aussi qu'un handle commençant par un chiffre (« 160x230-… »)
       reste un identifiant. */
    return 'product-' + String(handle).replace(/[^a-zA-Z0-9_-]/g, '');
  }

  function remember(handle) {
    try {
      window.sessionStorage.setItem(STORE, handle);
    } catch (e) {}
  }

  function recall() {
    try {
      return window.sessionStorage.getItem(STORE) || '';
    } catch (e) {
      return '';
    }
  }

  function clearNames() {
    document.querySelectorAll('[' + ATTR + ']').forEach(function (node) {
      node.style.viewTransitionName = '';
      node.removeAttribute(ATTR);
    });
  }

  function nameNode(node, handle) {
    if (!node || reduced()) return false;
    clearNames();
    node.style.viewTransitionName = nameFor(handle);
    node.setAttribute(ATTR, '');
    return true;
  }

  /* Un cadre ne se nomme que s'il est réellement à l'écran : nommer une image
     hors champ ferait entrer la fiche par un coin, ce qui est pire que rien. */
  function inView(node) {
    if (!node) return false;
    var rect = node.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return false;
    return rect.bottom > 0 && rect.top < window.innerHeight;
  }

  /* `skipTransition()` fait REJETER `ready` - et une promesse rejetée que
     personne n'écoute remonte en erreur de page. Le navigateur saute aussi de
     lui-même une transition dans des cas qu'il ne nous annonce pas. On accroche
     donc un écouteur vide à toutes les promesses de l'objet, dès qu'on le tient.
     Mesuré : quatre « Transition was skipped » en console sans cette ligne. */
  function silence(transition) {
    if (!transition) return;
    ['ready', 'finished', 'updateCallbackDone'].forEach(function (key) {
      var promise = transition[key];
      if (promise && typeof promise.catch === 'function') promise.catch(function () {});
    });
  }

  /* La destination d'une navigation sortante. `activation` fait partie de
     l'API Navigation, que Safari n'a pas encore : sans elle on ne sait pas où
     l'on va, et on laisse faire - le pire cas est un fondu de 200 ms. */
  function destinationOf(event) {
    var activation = event && event.activation;
    var entry = activation && activation.entry;
    return entry && entry.url ? entry.url : '';
  }

  function originOf(event) {
    var activation = (event && event.activation) || (window.navigation && window.navigation.activation);
    var from = activation && activation.from;
    return from && from.url ? from.url : '';
  }

  /* ══════════════════════════════════════════════════════════════════════
     CÔTÉ COLLECTION
     ══════════════════════════════════════════════════════════════════════ */

  function cardFrame(handle) {
    var list = document.getElementById('ProductsList');
    if (!list || !handle) return null;
    var link = list.querySelector('a[href*="/products/' + handle + '"]');
    var column = link && link.closest('.f-column');
    /* Le cadre, pas la balise `img` : c'est lui qui porte le rapport 4:5 et le
       rognage. Nommer l'image donnerait un instantané non rogné, et la photo
       « sauterait » d'un format à l'autre au départ du mouvement. */
    return column ? column.querySelector('.product-card__image--main') : null;
  }

  function onCollection() {
    return !!document.getElementById('ProductsList');
  }

  document.addEventListener(
    'click',
    function (event) {
      if (!onCollection()) return;
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;
      var link = target.closest('a[href*="/products/"]');
      var list = document.getElementById('ProductsList');
      if (!link || !list || !list.contains(link)) return;

      var match = (link.getAttribute('href') || '').split('?')[0].match(/\/products\/([^/?#]+)/);
      if (!match) return;

      remember(match[1]);
      nameNode(cardFrame(match[1]), match[1]);
    },
    true
  );

  /* ══════════════════════════════════════════════════════════════════════
     CÔTÉ FICHE
     ══════════════════════════════════════════════════════════════════════ */

  function gallery() {
    return document.querySelector('media-gallery[data-cm-vt]');
  }

  function galleryHandle() {
    var host = gallery();
    return host ? host.getAttribute('data-cm-vt') : '';
  }

  /* À l'arrivée, la première photo. Au départ, celle qu'on regarde - le
     carrousel a pu être glissé. Dans les deux cas, jamais une diapositive
     dupliquée par Swiper : deux nœuds au même nom annuleraient la paire. */
  function galleryFrame(active) {
    var host = gallery();
    if (!host) return null;
    var slides = host.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)');
    if (!slides.length) return null;
    var slide = null;
    if (active) {
      slide = host.querySelector('.product__media-item.swiper-slide-active:not(.swiper-slide-duplicate)');
    }
    if (!slide) slide = slides[0];
    return slide.querySelector('.product__media-item-container') || slide;
  }

  /* ══════════════════════════════════════════════════════════════════════
     LES DEUX PORTES : pageswap (on part) et pagereveal (on arrive)
     ══════════════════════════════════════════════════════════════════════ */

  window.addEventListener('pageswap', function (event) {
    if (!event.viewTransition) return;
    silence(event.viewTransition);

    var destination = destinationOf(event);
    var handle = '';

    if (onCollection()) {
      /* La destination est connue → on tranche. Inconnue (Safari) → on se fie
         au clic : `remember()` n'a été appelé que sur une carte produit, et le
         nom n'est posé que là. */
      if (destination && !isProductUrl(destination)) {
        event.viewTransition.skipTransition();
        clearNames();
        return;
      }
      if (destination) {
        var match = destination.split('?')[0].match(/\/products\/([^/?#]+)/);
        if (match) {
          remember(match[1]);
          if (!document.querySelector('[' + ATTR + ']')) nameNode(cardFrame(match[1]), match[1]);
        }
      }
      return;
    }

    handle = galleryHandle();
    if (!handle) return;

    if (destination && !isCollectionUrl(destination)) {
      event.viewTransition.skipTransition();
      clearNames();
      return;
    }
    if (!destination) {
      /* Sans API Navigation, on ne nomme rien au départ de la fiche : le retour
         se fait alors en fondu simple, jamais en image qui part de travers. */
      return;
    }

    var frame = galleryFrame(true);
    if (inView(frame)) {
      remember(handle);
      nameNode(frame, handle);
    } else {
      clearNames();
    }
  });

  window.addEventListener('pagereveal', function (event) {
    if (!event.viewTransition) return;
    silence(event.viewTransition);

    var from = originOf(event);
    var handle = recall();

    /* Effacer le nom dès la fin du mouvement : il ne doit rien peser sur la
       page une fois posée (un élément nommé est promu en couche à part). */
    event.viewTransition.finished.then(clearNames, clearNames);

    if (gallery()) {
      if (from && !isCollectionUrl(from)) {
        event.viewTransition.skipTransition();
        return;
      }
      if (handle && handle === galleryHandle()) {
        nameNode(galleryFrame(false), handle);
      }
      return;
    }

    if (onCollection()) {
      if (from && !isProductUrl(from)) {
        event.viewTransition.skipTransition();
        return;
      }
      /* Retour sur la grille : l'image ne revient sur sa carte que si cette
         carte est dans l'écran restauré. Elle l'est quand la page revient du
         bfcache (défilement intact) ; elle ne l'est pas quand la grille est
         reconstruite tranche par tranche par casemira-collection-more.js, et
         il ne reste alors que le fondu. */
      var frame = cardFrame(handle);
      if (inView(frame)) nameNode(frame, handle);
    }
  });
})();
